import { useCallback, useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { ShieldCheck, Send, Copy, Plus, Ban, ChevronDown, ChevronUp, Award, X, Eye, FileDown, Image as ImageIcon, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { DateDMYInput } from "@/components/DateDMYInput";

const DEFAULT_COVERAGE = [
  "Free repair or replacement if the treatment fails under normal use.",
  "Free consultation for any issue related to this procedure during the warranty period.",
  "Covers manufacturing defects and cementation failures.",
];
const DEFAULT_EXCLUSIONS = [
  "Damage caused by trauma, accident, or excessive force.",
  "Failure due to poor oral hygiene or missed routine cleanings.",
  "Modifications or repairs performed by another dentist.",
  "Bruxism (teeth grinding) without the prescribed night guard.",
  "Changes in the patient's medical condition that affect oral health.",
];

const isExpired = (validTill) => new Date(validTill) < new Date();

function EditableList({ label, items, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">{label}</label>
      <div className="space-y-1.5" data-testid={testid}>
        {items.map((it, i) => (
          <div key={`${label}-${i}`} className="flex items-start gap-2">
            <input value={it} onChange={(e) => { const next = [...items]; next[i] = e.target.value; onChange(next); }}
              className="flex-1 rounded-xl border border-[#E8EAE9] px-3 py-1.5 text-xs" />
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-ink-soft hover:text-[#B0521F] p-1">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...items, ""])} className="text-[11px] text-ink-mid hover:text-ink inline-flex items-center gap-1">
          <Plus className="w-3 h-3" /> Add {label.toLowerCase()} item
        </button>
      </div>
    </div>
  );
}

function WarrantyPreviewDialog({ warranty, open, onClose }) {
  if (!warranty) return null;
  const expired = isExpired(warranty.valid_till);
  const active = !warranty.voided && !expired;
  const statusLabel = warranty.voided ? "Voided" : expired ? "Expired" : "Active";
  const pillStyle = warranty.voided
    ? "bg-[#FFE9E9] text-[#8B1E1E]"
    : expired
    ? "bg-[#FFF6D9] text-[#8A6C1F]"
    : "bg-[#E8F0EB] text-[#2D6A4F]";
  const imgUrl = `${API}/public/warranties/${warranty.public_token}/image.png?t=${warranty.created_at}`;
  const pdfUrl = `${API}/public/warranties/${warranty.public_token}/pdf`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="warranty-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Warranty preview</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <div className="rounded-2xl overflow-hidden border border-[#E8EAE9] bg-linen/40 p-3">
              <img src={imgUrl} alt="Warranty card" className="w-full h-auto rounded-xl" data-testid="warranty-preview-image" />
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
              <a href={imgUrl} target="_blank" rel="noreferrer" download
                 data-testid="warranty-download-image"
                 className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-pine/20 text-ink hover:bg-linen">
                <ImageIcon className="w-3.5 h-3.5" /> Download image
              </a>
              <a href={pdfUrl} target="_blank" rel="noreferrer"
                 data-testid="warranty-download-pdf"
                 className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-pine/20 text-ink hover:bg-linen">
                <FileDown className="w-3.5 h-3.5" /> Open PDF
              </a>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest ${pillStyle}`}>{statusLabel}</span>
              <span className="text-xs text-ink-soft font-mono">{warranty.warranty_number}</span>
            </div>
            <div>
              <div className="overline text-ink-soft">Procedure</div>
              <div className="font-display text-xl text-ink">{warranty.procedure}{warranty.variant ? ` — ${warranty.variant}` : ""}</div>
              {warranty.tooth && <div className="text-xs text-ink-mid mt-0.5">Tooth #{warranty.tooth}</div>}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="overline text-ink-soft">Issued</div>
                <div className="font-mono text-ink">{warranty.issue_date}</div>
              </div>
              <div>
                <div className="overline text-ink-soft">Valid till</div>
                <div className={`font-mono ${active ? "text-ink" : "text-ink-mid"}`}>{warranty.valid_till}</div>
              </div>
            </div>
            <div className="text-xs">
              <div className="overline text-ink-soft">Coverage</div>
              <ul className="list-disc pl-5 mt-1 space-y-0.5 text-ink/80">
                {(warranty.coverage || []).map((c, i) => <li key={`cov-${i}`}>{c}</li>)}
              </ul>
            </div>
            <div className="text-xs rounded-xl bg-[#FFF8E7] border border-[#E5B34B] p-3">
              <div className="overline text-[#B0521F]">Exclusions</div>
              <ul className="list-disc pl-5 mt-1 space-y-0.5 text-ink/80">
                {(warranty.exclusions || []).map((e, i) => <li key={`exc-${i}`}>{e}</li>)}
              </ul>
            </div>
            {warranty.voided && warranty.voided_reason && (
              <div className="text-xs rounded-xl bg-[#FFF3F3] border border-[#F0A5A5] text-[#8B1E1E] p-3">
                <div className="overline">Voided reason</div>
                <div className="mt-1">{warranty.voided_reason}</div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function WarrantyCard({ patient }) {
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [preview, setPreview] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [form, setForm] = useState({
    procedure: "", tooth: "", variant: "",
    issue_date: new Date().toISOString().slice(0, 10),
    duration_years: 1, duration_months: 0,
    coverage: [...DEFAULT_COVERAGE], exclusions: [...DEFAULT_EXCLUSIONS], notes: "",
  });

  const load = useCallback(async () => {
    if (!patient?.id) return;
    const { data } = await api.get(`/patients/${patient.id}/warranties`);
    setHistory(data);
  }, [patient?.id]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.procedure.trim()) return toast.error("Enter the procedure name");
    if (Number(form.duration_years) === 0 && Number(form.duration_months) === 0) return toast.error("Set at least some duration");
    try {
      const { data } = await api.post(`/patients/${patient.id}/warranties`, {
        patient_id: patient.id,
        procedure: form.procedure.trim(),
        tooth: form.tooth || null,
        variant: form.variant || null,
        issue_date: form.issue_date,
        duration_years: Number(form.duration_years),
        duration_months: Number(form.duration_months),
        coverage: form.coverage.filter((c) => c.trim()),
        exclusions: form.exclusions.filter((e) => e.trim()),
        notes: form.notes || null,
      });
      toast.success(`Warranty ${data.warranty_number} issued`);
      setOpen(false);
      // Offer preview so clinic can review before sending
      setPreview(data);
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  const sendWithFormat = async (wid, format) => {
    setSendingId(wid);
    try {
      const { data } = await api.post(`/warranties/${wid}/send-whatsapp`, { format });
      const label = { all: "PDF + Image + Link", image: "Image", pdf: "PDF", link: "Link" }[format] || format;
      if (data.status === "mocked") {
        toast.info(`WhatsApp mocked (${label}) — configure GUPSHUP_* keys to send for real`);
      } else if (data.status === "failed") {
        toast.error(`WhatsApp failed for ${label}`);
      } else {
        toast.success(`Warranty ${label} sent via WhatsApp`);
      }
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
    finally { setSendingId(null); }
  };

  const copyLink = (w) => {
    navigator.clipboard?.writeText(window.location.origin + `/warranty/${w.public_token}`);
    toast.success("Link copied");
  };

  const voidWarranty = async (w) => {
    const reason = window.prompt(`Void warranty ${w.warranty_number}?\nReason:`);
    if (!reason) return;
    try {
      await api.post(`/warranties/${w.id}/void`, { reason });
      toast.success("Warranty voided");
      load();
    } catch (err) { toast.error(apiError(err, "Failed")); }
  };

  return (
    <div className="card-widget" data-testid="warranty-card">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-ink-mid" />
          <div className="overline text-ink-soft">Digital warranty cards</div>
        </div>
        <div className="flex items-center gap-2">
          {history.length > 0 && (
            <button data-testid="warranty-history-toggle" onClick={() => setHistoryOpen(!historyOpen)}
              className="text-xs text-ink-mid hover:text-ink inline-flex items-center gap-1">
              {history.length} on record {historyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="warranty-new-btn" className="btn-primary text-xs inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5" /> Issue warranty</button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">Issue warranty card</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3" data-testid="warranty-form">
                <input required data-testid="wty-procedure" placeholder="Procedure name (e.g., Zirconia Crown)"
                  value={form.procedure} onChange={(e) => setForm({ ...form, procedure: e.target.value })}
                  className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
                <div className="grid grid-cols-3 gap-3">
                  <input data-testid="wty-tooth" placeholder="Tooth # (optional)" value={form.tooth}
                    onChange={(e) => setForm({ ...form, tooth: e.target.value })}
                    className="rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm" />
                  <input data-testid="wty-variant" placeholder="Variant (optional)" value={form.variant}
                    onChange={(e) => setForm({ ...form, variant: e.target.value })}
                    className="col-span-2 rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Issue date</label>
                  <DateDMYInput data-testid="wty-issue-date" value={form.issue_date}
                    onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                    className="w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm font-mono" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Duration</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <input required type="number" min={0} max={99} data-testid="wty-years" value={form.duration_years}
                        onChange={(e) => setForm({ ...form, duration_years: e.target.value })}
                        className="w-full rounded-xl border border-[#E8EAE9] pl-4 pr-14 py-2 text-sm" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-ink-soft">years</span>
                    </div>
                    <div className="relative">
                      <input required type="number" min={0} max={11} data-testid="wty-months" value={form.duration_months}
                        onChange={(e) => setForm({ ...form, duration_months: e.target.value })}
                        className="w-full rounded-xl border border-[#E8EAE9] pl-4 pr-16 py-2 text-sm" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-ink-soft">months</span>
                    </div>
                  </div>
                </div>
                <EditableList label="Coverage" items={form.coverage} onChange={(v) => setForm({ ...form, coverage: v })} testid="wty-coverage" />
                <EditableList label="Exclusions" items={form.exclusions} onChange={(v) => setForm({ ...form, exclusions: v })} testid="wty-exclusions" />
                <textarea data-testid="wty-notes" placeholder="Notes (optional)" rows={2} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm resize-none" />
                <button data-testid="wty-submit" className="w-full btn-primary text-sm inline-flex items-center justify-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> Issue &amp; preview
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <p className="text-xs text-ink-mid mb-4">Issue a professional digital warranty card. Send it to the patient via WhatsApp as an image, PDF, or a link they can view on any device.</p>

      {historyOpen && (
        <div className="space-y-2" data-testid="warranty-history">
          {history.length === 0 ? (
            <div className="text-sm text-ink-soft text-center py-6">No warranty cards issued yet.</div>
          ) : history.map((w) => {
            const expired = isExpired(w.valid_till);
            const pillStyle = w.voided
              ? "bg-[#FFE9E9] text-[#8B1E1E]"
              : expired
              ? "bg-linen text-ink-mid"
              : "bg-[#E8F0EB] text-[#2D6A4F]";
            const status = w.voided ? "Voided" : expired ? "Expired" : "Active";
            const sending = sendingId === w.id;
            return (
              <div key={w.id} data-testid={`warranty-row-${w.id}`}
                className="border border-[#E8EAE9] rounded-2xl p-3 flex items-start gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-ink truncate">
                      {w.procedure}{w.variant ? ` — ${w.variant}` : ""}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest ${pillStyle}`}>{status}</span>
                  </div>
                  <div className="text-[11px] text-ink-soft mt-1 font-mono">
                    {w.warranty_number} · issued {w.issue_date} · valid till {w.valid_till}
                    {w.tooth && ` · Tooth ${w.tooth}`}
                  </div>
                  <div className="text-[10px] text-ink-soft mt-0.5">
                    Duration: {w.duration_years}y {w.duration_months}m
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button data-testid={`wty-view-${w.id}`} onClick={() => setPreview(w)}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-pine/20 text-ink hover:bg-linen inline-flex items-center gap-1"
                    title="View / preview">
                    <Eye className="w-3 h-3" /> View
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`wty-send-${w.id}`} disabled={sending}
                        className="text-[11px] px-2.5 py-1 rounded-full bg-[#25D366] text-white hover:bg-[#1DAF54] inline-flex items-center gap-1 disabled:opacity-60">
                        <Send className="w-3 h-3" /> {sending ? "Sending…" : "Send"}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white">
                      <DropdownMenuItem data-testid={`wty-send-all-${w.id}`} onClick={() => sendWithFormat(w.id, "all")}>
                        <MessageCircle className="w-3.5 h-3.5 mr-2" /> PDF + Image + Link
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid={`wty-send-image-${w.id}`} onClick={() => sendWithFormat(w.id, "image")}>
                        <ImageIcon className="w-3.5 h-3.5 mr-2" /> Image only
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`wty-send-pdf-${w.id}`} onClick={() => sendWithFormat(w.id, "pdf")}>
                        <FileDown className="w-3.5 h-3.5 mr-2" /> PDF only
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`wty-send-link-${w.id}`} onClick={() => sendWithFormat(w.id, "link")}>
                        <Send className="w-3.5 h-3.5 mr-2" /> Text link
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <button data-testid={`wty-copy-${w.id}`} onClick={() => copyLink(w)}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-pine/20 text-ink-mid hover:bg-linen inline-flex items-center gap-1"
                    title="Copy public link">
                    <Copy className="w-3 h-3" />
                  </button>
                  {!w.voided && (
                    <button data-testid={`wty-void-${w.id}`} onClick={() => voidWarranty(w)}
                      className="text-[11px] px-2.5 py-1 rounded-full border border-[#F0A5A5] text-[#8B1E1E] hover:bg-[#FFF3F3] inline-flex items-center gap-1"
                      title="Void warranty">
                      <Ban className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <WarrantyPreviewDialog warranty={preview} open={!!preview} onClose={() => setPreview(null)} />
    </div>
  );
}
